import TableOnly from "components/DataTable/TableOnly";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import PaginationWithState from "components/Pagination/PaginationWithState";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import { EfsInvoicePdf } from "./EfsInvoicePdf";
import EfsInvoiceAddUpdate from "./EfsInvoiceAddUpdate";
import AddEfsInvoice from "./AddEfsInvoice";
import { Link } from "react-router-dom";

export default function EfsInvoice() {
  const dispatch = useDispatch();
  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });
  const [efsInvoiceData, setEfsInvoiceData] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedItemForPdf, setSelectedItemForPdf] = useState(null);

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  /*   All Functions
   ********************************************* */
  //get Data (uses POST /manufacturer/efs-invoices?page=&limit=)
  const getEfsInvoiceData = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);
        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
          ? query
          : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/manufacturer/efs-invoices?page=" + usedPage + "&limit=" + pageLimit,
          {
            query: usedQuery,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
          },
          { headers: authHeader() }
        );

        if (data?.success) {
          // API returns { success, data, total, page, limit, totalPages }
          setEfsInvoiceData(data?.data || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            })
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [pageNumber, searchTerm, pageLimit, filterBy.value]
  );

  // Delete EfsInvoice
  const deleteEfsInvoiceData = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete("/manufacturer/efs-invoice/" + id, {
        headers: authHeader(),
      });
      if (data?.success) {
        // refresh current page
        await getEfsInvoiceData();
        dispatch(
          setNotification({
            message: data?.message || "Deleted",
            type: "success",
          })
        );
        return;
      }
      dispatch(
        setNotification({
          message: data?.message || "Failed to delete",
          type: "error",
        })
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      getEfsInvoiceData(
        searchTerm && searchTerm !== ""
          ? { query: searchTerm, page: pageNumber }
          : { clearSearch: true, page: pageNumber }
      );
    }, 50);
    return () => clearTimeout(timeout);
  }, [pageNumber, pageLimit, filterBy]);

  /*   Table functions
   ********************************************* */
  let filters = [
    { value: "name", name: "Data Name" },
    { value: "contactName", name: "Contact Name" },
    { value: "contactNumber", name: "Contact Number" },
  ];

  const buttons = () => (
    <div className=" d-flex justify-content-end ">
      {/* FilterBy */}
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy ? filterBy.name : "Filter by All"}
      </button>
      <div className="dropdown-menu">
        <button
          onClick={() => setFilterBy({ name: "Filter by All", value: null })}
          className="dropdown-item"
        >
          Filter by All
        </button>
        {filters.map((item, index) => (
          <button
            key={index}
            onClick={() => setFilterBy(item)}
            className="dropdown-item"
          >
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  const table_head = [
    { name: "ID", value: "invId" },
    { name: "Invoice No", value: "invoiceNo" },
    { name: "Shipment Type", value: "shipmentType" },
    { name: "Destination", value: "destination" },
    { name: "# of Packages", value: "noOfPackages" },
    { name: "AWB No", value: "awbNo" },
    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item, index) => (
    <tr className="" key={item.id}>
      <td className="align-middle py-1">{item?.invId}</td>
      <td className="align-middle py-1">{item?.invoiceNo}</td>
      <td className="align-middle py-1">{item?.shipmentType?.toUpperCase()}</td>
      <td className="align-middle py-1">{item?.destination}</td>
      <td className="align-middle py-1">{item?.noOfPackages}</td>
      <td className="align-middle py-1">{item?.awbNo}</td>
      <td className="align-middle py-1 text-end table-action">
        <button
          disabled={loading}
          onClick={() => {
            setSelectedItemForPdf(item);
            setShowPdfModal(true);
          }}
          className="btn btn-sm btn-info py-0 px-2"
          title="View PDF"
        >
          PDF
        </button>

        <Link
          disabled={loading}
          to={`/manufacturer/efs-invoice/details/${item.invId}`}
          className="btn btn-sm px-1"
        >
          <i className="mdi mdi-pencil"></i>
        </Link>

        <DeleteModalButton
          btnClass={"btn btn-sm  px-1"}
          deleteFunc={deleteEfsInvoiceData}
          deleteParams={item.id}
          disableConditions={loading}
        />
      </td>
    </tr>
  );

  return (
    <div className="px-2 py-3" style={{ marginLeft: 260, minHeight: "100vh" }}>
      {loading && <Spinner />}
      {/*   Table
       ********************************************* */}

      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div className="ps-2">
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex">
          {buttons()}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getEfsInvoiceData({ query: searchTerm, page: 1 });
              } else {
                getEfsInvoiceData({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search by name..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getEfsInvoiceData({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          {/* Add button moved next to search for consistent layout */}
          <div className="">
            <button
              onClick={() => {
                setShowAddModal(true);
              }}
              type="button"
              className="btn btn-primary  ms-1"
            >
              Add Invoice
            </button>
          </div>
        </div>
      </div>

      <div
        className="px-1 flex-grow-1 d-flex"
        style={{ minHeight: "50vh", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={efsInvoiceData}
            disableSorting={true}
            actionsOnEnd={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      {/*   PDF Modal
       ********************************************* */}
      <Modal
        show={showPdfModal}
        size="xl"
        onHide={() => {
          setShowPdfModal(false);
          setSelectedItemForPdf(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Invoice PDF - {selectedItemForPdf?.invoiceNo || "N/A"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: "80vh" }}>
          {selectedItemForPdf && (
            <PDFViewer
              style={{ width: "100%", height: "100%", border: "none" }}
            >
              <EfsInvoicePdf
                data={selectedItemForPdf}
                efsNo={"EFS-SEXP-3926897-228-19-03-2023"}
              />
            </PDFViewer>
          )}
        </Modal.Body>
      </Modal>

      {/*   Add Modal
       ********************************************* */}
      <Modal
        show={showAddModal}
        // size="sm"
        onHide={() => setShowAddModal(false)}
      >
        <Modal.Body className="shadow-lg bg-white" style={{ borderRadius: 15 }}>
          <AddEfsInvoice showModal={setShowAddModal} />
        </Modal.Body>
      </Modal>
    </div>
  );
}
